import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, getCsrfToken, unwrap } from "../api/client";
import type { components } from "../api/schema";

type Status = components["schemas"]["UpdateStatus"];
type Release = components["schemas"]["UpdateRelease"];

const KEY = ["updates"] as const;

function base64(buf: ArrayBuffer): string {
  let s = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000)
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** The signed update channel (plan 5.2), on the Settings page. */
export function UpdatesSection() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/updates")),
  });
  const done = (s: Status) => qc.setQueryData(KEY, s);
  const check = useMutation({
    mutationFn: () => unwrap(api.POST("/api/updates/check")),
    onSuccess: done,
  });
  const install = useMutation({
    mutationFn: () => unwrap(api.POST("/api/updates/install")),
    onSuccess: done,
  });
  const s = status.data;
  if (status.isError)
    return <Alert severity="info">{status.error.message}</Alert>;
  if (!s) return null;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Updates"
    >
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          Updates
        </Typography>
        <Typography variant="body2">
          This is AVGV {s.current}.{" "}
          {s.enabled
            ? "Releases are checked against AVGV's release key before anything is kept or installed."
            : "This build has no release key, so it can't check releases."}
        </Typography>
        {s.enabled && (
          <>
            <Source status={s} onSaved={done} />
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                disabled={!s.source || check.isPending}
                onClick={() => check.mutate()}
              >
                Check for updates
              </Button>
              {s.checked_at && (
                <Typography variant="body2" color="text.secondary">
                  Last checked {s.checked_at.slice(0, 16).replace("T", " ")} UTC
                  {!s.latest && !s.error && ": this is the latest release"}
                </Typography>
              )}
            </Stack>
            {check.isError && (
              <Alert severity="error">{check.error.message}</Alert>
            )}
            {s.error && <Alert severity="warning">{s.error}</Alert>}
            {s.latest && !s.ready && <Offered release={s.latest} />}
            {s.ready && (
              <Ready
                status={s}
                release={s.ready}
                installing={install.isPending}
                onInstall={() => install.mutate()}
              />
            )}
            {install.isError && (
              <Alert severity="error">{install.error.message}</Alert>
            )}
            {install.isSuccess && (
              <Alert severity="success">
                The installer has started; AVGV closes for it.
              </Alert>
            )}
            {s.platform !== "server-deb" && (
              <FromFolder status={s} onDone={done} />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function Source({
  status,
  onSaved,
}: {
  status: Status;
  onSaved: (s: Status) => void;
}) {
  const [url, setUrl] = useState(status.source);
  const [ca, setCa] = useState("");
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/api/updates/source", {
          body: { url, ca_pem: ca.trim() ? ca : null },
        }),
      ),
    onSuccess: onSaved,
  });
  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        <TextField
          label="Update address"
          size="small"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          sx={{ flex: 1, minWidth: 280 }}
          helperText="An https:// folder holding avgv-update.json; empty if this computer has no internet"
        />
        <Button
          variant="outlined"
          disabled={save.isPending || url === status.source}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </Stack>
      <TextField
        label="CA certificate (PEM), for an update server with its own certificates"
        size="small"
        multiline
        minRows={2}
        value={ca}
        onChange={(e) => setCa(e.target.value)}
        helperText={
          status.source_has_ca ? "One is set; paste another to replace it." : ""
        }
      />
      {save.isError && <Alert severity="error">{save.error.message}</Alert>}
    </Stack>
  );
}

function Offered({ release }: { release: Release }) {
  return (
    <Alert severity="info">
      AVGV {release.version} ({release.released}) is available
      {release.file ? "" : ", but has nothing for this computer"}.
      {release.notes && (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
          {release.notes}
        </Typography>
      )}
    </Alert>
  );
}

function Ready({
  status,
  release,
  installing,
  onInstall,
}: {
  status: Status;
  release: Release;
  installing: boolean;
  onInstall: () => void;
}) {
  const how: Record<string, string> = {
    "linux-deb": `Install it with: sudo apt install ./${release.file}`,
    "linux-rpm": `Install it with: sudo dnf install ./${release.file}`,
  };
  return (
    <Alert
      severity="success"
      action={
        status.installs && (
          <Button color="inherit" disabled={installing} onClick={onInstall}>
            Install and restart
          </Button>
        )
      }
    >
      AVGV {release.version} ({release.released}) has been checked and is ready
      to install.
      {!status.installs &&
        ` It is in the updates folder of AVGV's data. ${how[status.platform] ?? ""}`}
      {release.notes && (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
          {release.notes}
        </Typography>
      )}
    </Alert>
  );
}

/** A release copied from elsewhere: its manifest, signature and installer. */
function FromFolder({
  status,
  onDone,
}: {
  status: Status;
  onDone: (s: Status) => void;
}) {
  const give = useMutation({
    mutationFn: async (files: File[]) => {
      const named = (n: string) => files.find((f) => f.name === n);
      const manifest = named("avgv-update.json");
      const signature = named("avgv-update.json.sig");
      if (!manifest || !signature)
        throw new Error("Choose avgv-update.json and avgv-update.json.sig too");
      const pending = await unwrap(
        api.POST("/api/updates/import/manifest", {
          body: {
            manifest: base64(await manifest.arrayBuffer()),
            signature: base64(await signature.arrayBuffer()),
          },
        }),
      );
      const file = pending.pending?.file && named(pending.pending.file);
      if (!file)
        throw new Error(
          `Choose ${pending.pending?.file ?? "this computer's installer"} too`,
        );
      const token = getCsrfToken();
      const r = await fetch("/api/updates/import/file", {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
        body: file,
      });
      const body = (await r.json()) as Status & { message?: string };
      if (!r.ok) throw new Error(body.message ?? `HTTP ${r.status}`);
      return body;
    },
    onSuccess: onDone,
  });
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        Without internet: copy a release folder here (on a USB stick, say), then
        choose its avgv-update.json, avgv-update.json.sig and the installer for
        this computer ({status.platform}).
      </Typography>
      <div>
        <Button variant="outlined" component="label" disabled={give.isPending}>
          Install from a folder…
          <input
            type="file"
            multiple
            hidden
            aria-label="Release files"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) give.mutate(files);
              e.target.value = "";
            }}
          />
        </Button>
      </div>
      {give.isError && <Alert severity="error">{give.error.message}</Alert>}
    </Stack>
  );
}
